import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowLeft, Save, Copy, ExternalLink, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { inputCls, areaCls, Field, ImageField } from "./fields";
import { RichEditor } from "./RichEditor";

const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const blocksToHtml = (blocks) => (blocks || []).map((b) => {
  if (b.type === "heading") return b.text ? `<h2>${esc(b.text)}</h2>` : "";
  if (b.type === "image") return b.src ? `<img src="${esc(b.src)}" alt="" />` : "";
  if (b.type === "list") {
    const items = (b.items || []).filter((x) => String(x).trim());
    return items.length ? `<ul>${items.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>` : "";
  }
  if (b.type === "quote") return b.text ? `<blockquote><p>${esc(b.text)}</p></blockquote>` : "";
  return b.text ? `<p>${esc(b.text)}</p>` : "";
}).filter(Boolean).join("");

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600",
  scheduled: "bg-amber-100 text-amber-700",
  published: "bg-emerald-100 text-emerald-700",
};

function PostEditor({ post, onSaved, onCancel }) {
  const [p, setP] = useState({ ...post, body_html: post.body_html || blocksToHtml(post.blocks), tags_text: (post.tags || []).join(", ") });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setP((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    if (!p.title?.trim()) {
      toast.error("Give the post a title");
      return;
    }
    if (p.status === "scheduled" && !p.publish_at) {
      toast.error("Pick the date and time to publish");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: p.title, slug: p.slug, excerpt: p.excerpt, cover_image: p.cover_image,
        body_html: p.body_html, tags: p.tags_text || "", status: p.status, publish_at: p.publish_at || null,
      };
      const { data } = p.id
        ? await api.put(`/site/blog/admin/${p.id}`, payload)
        : await api.post("/site/blog/admin", payload);
      toast.success(p.status === "scheduled" ? `Scheduled for ${fmtWhen(data.publish_at)}` : p.status === "published" ? "Published — it's live on the blog" : "Draft saved");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const copyShare = async () => {
    const url = `${window.location.origin}/blog/${p.slug || ""}`;
    const text = `${p.title}\n\n${p.excerpt || ""}\n\n${url}`.trim();
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Share text copied — paste it into Facebook, Instagram, anywhere");
    } catch {
      toast.error(url);
    }
  };

  return (
    <div className="space-y-4" data-testid="blog-editor">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onCancel} className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 text-sm font-bold inline-flex items-center gap-1.5 bg-white" data-testid="blog-back">
          <ArrowLeft className="h-4 w-4" /> All posts
        </button>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={copyShare} className="h-10 px-3 rounded-lg border border-slate-300 text-[#005EB8] text-sm font-bold inline-flex items-center gap-1.5 bg-white" data-testid="blog-copy-share">
            <Copy className="h-4 w-4" /> Copy share text
          </button>
          <button type="button" onClick={save} disabled={saving} className="h-10 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center gap-1.5" data-testid="blog-save">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save post"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <Field label="Title"><input className={inputCls} value={p.title || ""} onChange={(e) => upd("title", e.target.value)} data-testid="blog-title" /></Field>
        <Field label="Link (slug) — leave as-is unless you need to change it">
          <input className={inputCls} value={p.slug || ""} onChange={(e) => upd("slug", e.target.value)} data-testid="blog-slug" />
        </Field>
        <Field label="Short summary (shown on the blog list and social shares)">
          <textarea rows={2} className={areaCls} value={p.excerpt || ""} onChange={(e) => upd("excerpt", e.target.value)} data-testid="blog-excerpt" />
        </Field>
        <Field label="Keywords / topics (comma-separated — shown under the post and used by Google)">
          <input className={inputCls} value={p.tags_text || ""} placeholder="e.g. event medical cover, festival first aid, sports medics"
            onChange={(e) => upd("tags_text", e.target.value)} data-testid="blog-tags" />
        </Field>
        <ImageField value={p.cover_image || ""} onChange={(v) => upd("cover_image", v)} testid="blog-cover" label="Cover photo" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="blog-blocks">
        <h3 className="font-heading text-base font-bold text-[#0F2547]">Post content</h3>
        <RichEditor value={p.body_html || ""} onChange={(v) => upd("body_html", v)} testid="blog-body" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="blog-publish">
        <h3 className="font-heading text-base font-bold text-[#0F2547]">Publishing</h3>
        <Field label="Status">
          <select className={inputCls} value={p.status || "draft"} onChange={(e) => upd("status", e.target.value)} data-testid="blog-status">
            <option value="draft">Draft — keep hidden</option>
            <option value="published">Publish now</option>
            <option value="scheduled">Schedule — publish automatically at a set time</option>
          </select>
        </Field>
        {p.status === "scheduled" && (
          <Field label="Publish date & time">
            <input type="datetime-local" className={inputCls} value={toLocalInput(p.publish_at)}
              onChange={(e) => upd("publish_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
              data-testid="blog-publish-at" />
          </Field>
        )}
        {p.status === "scheduled" && p.publish_at && (
          <p className="text-xs text-slate-500">Goes live automatically at {fmtWhen(p.publish_at)} — checked every 15 minutes.</p>
        )}
        {p.published_at && <p className="text-xs text-slate-500">Published {fmtWhen(p.published_at)}</p>}
      </div>
    </div>
  );
}

export default function BlogManager() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/site/blog/admin");
      setPosts(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
      setPosts([]);
    }
  };
  useEffect(() => { load(); }, []);

  const share = async (p) => {
    try {
      const { data } = await api.post(`/site/blog/admin/${p.id}/share`);
      toast.success(data.message || "Shared to your social pages");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (p) => {
    try {
      await api.delete(`/site/blog/admin/${p.id}`);
      toast.success("Post deleted");
      setPosts((cur) => cur.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (editing) {
    return (
      <PostEditor post={editing}
        onSaved={(saved) => { setEditing(null); load(); }}
        onCancel={() => setEditing(null)} />
    );
  }

  return (
    <div className="space-y-4" data-testid="blog-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-slate-500 max-w-lg">
          Write posts for the public blog at <span className="font-semibold text-slate-700">/blog</span>. Publish straight away or schedule a date and time.
        </p>
        <button type="button"
          onClick={() => setEditing({ title: "", slug: "", excerpt: "", cover_image: "", body_html: "", status: "draft", publish_at: "" })}
          className="h-10 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-sm font-bold inline-flex items-center gap-1.5" data-testid="blog-new">
          <Plus className="h-4 w-4" /> New post
        </button>
      </div>
      {!posts ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center" data-testid="blog-list-empty">
          <p className="text-sm text-slate-500">No posts yet — write your first one.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {posts.map((p, i) => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3" data-testid={`blog-row-${i}`}>
              {p.cover_image && <img src={p.cover_image} alt="" className="h-11 w-16 object-cover rounded-lg border border-slate-200 shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm font-bold text-[#0F2547] truncate">{p.title}</p>
                <p className="text-xs text-slate-400">
                  {p.status === "published" ? `Published ${fmtWhen(p.published_at)}` : p.status === "scheduled" ? `Scheduled for ${fmtWhen(p.publish_at)}` : "Draft"}
                  {" · "}/blog/{p.slug}
                </p>
              </div>
              <span className={`ml-auto px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide shrink-0 ${STATUS_STYLES[p.status] || STATUS_STYLES.draft}`} data-testid={`blog-status-${i}`}>
                {p.status}
              </span>
              {p.status === "published" && (
                p.social_status === "shared" ? (
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-teal-100 text-teal-700 shrink-0" data-testid={`blog-shared-${i}`}>Shared</span>
                ) : (
                  <button type="button" onClick={() => share(p)} data-testid={`blog-share-${i}`}
                    className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 shrink-0">
                    <Share2 className="h-3.5 w-3.5" /> {p.social_status === "error" ? "Retry share" : "Share"}
                  </button>
                )
              )}
              {p.status === "published" && (
                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" aria-label="View post" data-testid={`blog-view-${i}`}
                  className="h-9 w-9 grid place-items-center rounded-lg border border-slate-200 text-slate-500 hover:border-slate-400 shrink-0">
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
              <button type="button" onClick={() => setEditing(p)} data-testid={`blog-edit-${i}`}
                className="h-9 px-3 rounded-lg border border-slate-300 text-[#005EB8] text-xs font-bold shrink-0">Edit</button>
              <button type="button" onClick={() => del(p)} aria-label="Delete post" data-testid={`blog-del-${i}`}
                className="h-9 w-9 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 shrink-0">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
